import React, { useState, useEffect, useCallback } from 'react';
import {
  compose,
  branch,
  renderComponent,
  withHandlers,
  withProps,
  withState,
} from 'recompose';
import { Select, Range, Input, Values } from 'components/Form';
import { Select as SelectComponent } from 'components/Form/Select';
import { isArray } from 'lodash';
import { FieldArray } from 'redux-form';
import { Rhythm } from 'components/Layout/Rhythm';
import { searchApiCall } from 'redux/shelf/api';
import { Typo } from 'components/Layout';
import Autocomplete from '../Autocomplete';

const getFacets = searchApiCall('post', '/facet/values');
const fetchFacets = (data) =>
  getFacets(data).then(
    (res) =>
      res.data &&
      res.data.facets[0].values.reduce(
        (acc, item) => ({
          ...acc,
          [item.value]: `${item.value} (${item.count})`,
        }),
        {}
      )
  );

const drawNesting = (values, lvl, isLast) => {
  let res = '';
  if (values.length - lvl) res += Array(values.length - lvl).join('┄');
  if (lvl > 1) res += Array(lvl).join('┆┄');
  res += (isLast ? '┗' : '┣') + '━';
  return res;
};

const reduceCategoriesChildren = (items, values?, level = 0) =>
  items.reduce(
    (acc, item, index) => ({
      ...acc,
      [values ? [...values, item.value].join('>') : item.value]:
        (values
          ? `${drawNesting(values, level, index === items.length - 1)} ${
              item.value
            }`
          : item.value) + ` (${item.count})`,
      ...(item.has_children &&
        reduceCategoriesChildren(
          item.children,
          [...(values || []), item.value],
          level + 1
        )),
    }),
    {}
  );

const fetchCategoryFacets = (data) =>
  getFacets(data).then(
    (res) => res.data && reduceCategoriesChildren(res.data.facets[0].values)
  );

const emptyArray = {};
const parseNumbers = (val) => (val && parseFloat(val)) || 0;
const mapRatingValues = {
  get: (val) => val[0].max,
  set: (val) => [{ max: val, min: val }],
};

const formatTextFacet = (v) => v.map((item) => item.value);

export const text = ({ name, title, data, label, normalize }) => (
  <Select
    searchable
    multiple
    label={label}
    name={name}
    format={(v) => v?.map((item) => item.value || item)}
    normalize={(v) =>
      (normalize && normalize(v)) || (v && v.map((value) => ({ value })))
    }
    fetch={(_prefix) => {
      const prefix = typeof _prefix === 'string' ? _prefix : void 0;
      return fetchFacets({ ...data, prefix, limit: 50 });
    }}
  />
);

export const RangeComponent = ({
  fields,
  title,
  horizontal,
  inputStyle,
  style,
}) =>
  fields.map((f) => (
    <Rhythm key={f} vertical={!horizontal}>
      <Input
        inline
        style={style}
        inputStyle={inputStyle}
        name={`${f}.from`}
        label={`Min${title === 'discount' ? ' %' : ''}`}
        type="number"
        normalize={parseNumbers}
      />
      <Input
        inline
        style={style}
        inputStyle={inputStyle}
        name={`${f}.to`}
        label={`Max${title === 'discount' ? ' %' : ''}`}
        type="number"
        normalize={parseNumbers}
      />
    </Rhythm>
  ));

const RatingComponent = compose(
  withState('selectedType', 'selectType', ({ formData }) =>
    formData[0].min ? 'min' : 'max'
  ),
  withHandlers({
    format:
      ({ selectedType }) =>
      (val) =>
        val[0][selectedType],
    normalize:
      ({ selectedType }) =>
      (val) =>
        [{ [selectedType]: Number(val) }],
    selectType:
      ({ selectType }) =>
      (type) =>
        selectType(type),
  })
)(({ field, selectedType, selectType, format, normalize }: any) => (
  <Rhythm vertical>
    <SelectComponent
      value={selectedType}
      onChange={selectType}
      options={{
        min: 'Minimum rating',
        max: 'Maximum rating',
      }}
      label="Show only products with:"
    />
    <Select
      inline
      name={field}
      label="Stars"
      style={{ width: 200 }}
      format={format}
      normalize={normalize}
      options={[1, 2, 3, 4, 5]}
    />
  </Rhythm>
));

export const range = branch(
  ({ name }) => name === 'reviews.average_score',
  renderComponent(RatingComponent),
  withProps({ component: RangeComponent })
)(FieldArray);

export const category = ({ name, title, data, label }) => (
  <Select
    searchable
    label={label}
    name={name}
    format={(v) => v && v[0] && v[0].value}
    normalize={(value) => [{ value }]}
    fetch={() => fetchCategoryFacets(data)}
  />
);

export const product = ({ normalize, format, multiple = true, ...rest }) => {
  return (
    <Autocomplete
      {...rest}
      multiple={multiple}
      allowCreate
      icon="search"
      placeholder="Search for product..."
      format={(v) =>
        (format && format(v)) || (v && v.map((item) => item.value || item))
      }
      normalize={(v) =>
        (normalize && normalize(v)) || (v && v.map((value) => ({ value })))
      }
    />
  );
};
